import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MapPin,
  UserRound,
  Users,
} from "lucide-react";

import "./HubAcre.css";
import "./HubAgenda.css";

const API_URL = "/api/hub/agenda";

const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const HUB_SPACE_LABELS = {
  a_definir: "Espaço a definir",
  sala_reuniao: "Sala de reunião",
  palco: "Palco",
  mesas: "Mesas",
  outro: "Outro",
};


const WEEKDAYS = [
  "Dom",
  "Seg",
  "Ter",
  "Qua",
  "Qui",
  "Sex",
  "Sáb",
];

const CATEGORY_LABELS = {
  evento: "Evento",
  workshop: "Workshop",
  oficina: "Oficina",
  reuniao: "Reunião",
  representacao: "Representação",
  consultoria: "Consultoria",
  atendimento: "Atendimento",
  formacao: "Formação",
  outro: "Outro",
};

function parseDate(value) {
  if (!value) {
    return null;
  }

  const [year, month, day] = value
    .split("-")
    .map(Number);

  return new Date(
    year,
    month - 1,
    day,
    12,
    0,
    0
  );
}

function isoDate(date) {
  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");
  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function eventOccursOn(item, date) {
  const target = isoDate(date);

  const start = item.data_inicio;
  const end =
    item.data_fim ||
    item.data_inicio;

  return (
    target >= start &&
    target <= end
  );
}

function formatDate(value) {
  const date = parseDate(value);

  if (!date) {
    return "";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  ).format(date);
}

function formatShortDate(value) {
  const date = parseDate(value);

  if (!date) {
    return "";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "short",
    }
  ).format(date);
}

function formatPeriod(item) {
  if (
    item.data_fim &&
    item.data_fim !== item.data_inicio
  ) {
    return (
      `${formatShortDate(item.data_inicio)} a ` +
      `${formatShortDate(item.data_fim)}`
    );
  }

  return formatDate(item.data_inicio);
}

function formatTime(item) {
  if (item.horario_a_definir) {
    return "Horário a definir";
  }

  if (
    item.hora_inicio &&
    item.hora_fim
  ) {
    return (
      `${item.hora_inicio} às ` +
      `${item.hora_fim}`
    );
  }

  if (item.hora_inicio) {
    return item.hora_inicio;
  }

  return "Horário não informado";
}

function HubAgenda() {
  const today = useMemo(
    () => new Date(),
    []
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState("");

  const [currentDate, setCurrentDate] =
    useState(
      new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      )
    );

  const [selectedDay, setSelectedDay] =
    useState(null);

  const [localFilter, setLocalFilter] =
    useState("todos");

  const [
    categoryFilter,
    setCategoryFilter,
  ] = useState("todos");

  useEffect(() => {
    async function loadAgenda() {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          API_URL
        );

        if (!response.ok) {
          throw new Error(
            "Não foi possível carregar a agenda."
          );
        }

        const data =
          await response.json();

        setItems(
          Array.isArray(data.agenda)
            ? data.agenda
            : []
        );
      } catch (err) {
        setError(
          err.message ||
          "Não foi possível carregar a agenda."
        );
      } finally {
        setLoading(false);
      }
    }

    loadAgenda();
  }, []);

  const categories = useMemo(() => {
    return [
      ...new Set(
        items
          .map((item) => item.categoria)
          .filter(Boolean)
      ),
    ].sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesLocal =
        localFilter === "todos" ||
        item.tipo_local === localFilter;

      const matchesCategory =
        categoryFilter === "todos" ||
        item.categoria === categoryFilter;

      return (
        matchesLocal &&
        matchesCategory
      );
    });
  }, [
    items,
    localFilter,
    categoryFilter,
  ]);

  const days = useMemo(() => {
    const year =
      currentDate.getFullYear();

    const month =
      currentDate.getMonth();

    const first =
      new Date(year, month, 1);

    const last =
      new Date(year, month + 1, 0);

    const cells = [];

    for (
      let i = 0;
      i < first.getDay();
      i += 1
    ) {
      cells.push(null);
    }

    for (
      let day = 1;
      day <= last.getDate();
      day += 1
    ) {
      cells.push(
        new Date(
          year,
          month,
          day,
          12
        )
      );
    }

    while (
      cells.length % 7 !== 0
    ) {
      cells.push(null);
    }

    return cells;
  }, [currentDate]);

  const monthItems = useMemo(() => {
    const year =
      currentDate.getFullYear();

    const month =
      currentDate.getMonth();

    const monthStart = isoDate(
      new Date(year, month, 1, 12)
    );

    const monthEnd = isoDate(
      new Date(
        year,
        month + 1,
        0,
        12
      )
    );

    return filteredItems.filter(
      (item) => {
        const start =
          item.data_inicio;

        const end =
          item.data_fim ||
          item.data_inicio;

        return (
          start <= monthEnd &&
          end >= monthStart
        );
      }
    );
  }, [
    currentDate,
    filteredItems,
  ]);

  const visibleItems = useMemo(() => {
    if (!selectedDay) {
      return monthItems;
    }

    return monthItems.filter(
      (item) =>
        eventOccursOn(
          item,
          selectedDay
        )
    );
  }, [
    monthItems,
    selectedDay,
  ]);

  function previousMonth() {
    setCurrentDate((current) =>
      new Date(
        current.getFullYear(),
        current.getMonth() - 1,
        1
      )
    );

    setSelectedDay(null);
  }

  function nextMonth() {
    setCurrentDate((current) =>
      new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        1
      )
    );

    setSelectedDay(null);
  }

  function goToday() {
    setCurrentDate(
      new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      )
    );

    setSelectedDay(today);
  }

  return (
    <main
      className="hub-page hub-agenda-page notranslate"
      translate="no"
    >
      <header className="hub-nav">
        <div className="hub-container hub-nav-inner">
          <a
            href="/hubacre"
            className="hub-nav-logo"
          >
            <img
              src="/hubacre/logo-horizontal.png"
              alt="HUB Acre"
            />
          </a>

          <nav>
            <a href="/hubacre">
              Início
            </a>

            <a
              href="/hubacre/agenda"
              className="hub-agenda-active"
            >
              Agenda
            </a>

            <a
              href="/hubacre#programas"
            >
              Programas
            </a>

            <a
              href="/hubacre#atendimento"
              className="hub-nav-cta"
            >
              Falar com o HUB
            </a>
          </nav>
        </div>
      </header>

      <section className="agenda-hero">
        <div className="hub-container">
          <a
            href="/hubacre"
            className="agenda-back"
          >
            <ArrowLeft size={18} />
            Voltar para o HUB Acre
          </a>

          <div className="agenda-hero-copy">
            <span className="hub-eyebrow">
              HUB ACRE
            </span>

            <h1>
              Agenda de
              <strong> atividades</strong>
            </h1>

            <p>
              Acompanhe eventos, oficinas,
              workshops, reuniões e demais
              atividades realizadas pelo HUB Acre
              e seus programas.
            </p>
          </div>
        </div>
      </section>

      <section className="agenda-content">
        <div className="hub-container">

          <div className="agenda-filters">
            <div className="agenda-filter-group">
              <span>Local</span>

              <div className="agenda-filter-buttons">
                {[
                  ["todos", "Todas"],
                  ["hub", "Internas no HUB"],
                  ["externa", "Externas"],
                ].map(
                  ([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={
                        localFilter ===
                        value
                          ? "active"
                          : ""
                      }
                      onClick={() =>
                        setLocalFilter(
                          value
                        )
                      }
                    >
                      {label}
                    </button>
                  )
                )}
              </div>
            </div>

            <div className="agenda-filter-group">
              <label
                htmlFor="agenda-category"
              >
                Categoria
              </label>

              <select
                id="agenda-category"
                value={categoryFilter}
                onChange={(event) =>
                  setCategoryFilter(
                    event.target.value
                  )
                }
              >
                <option value="todos">
                  Todas as categorias
                </option>

                {categories.map(
                  (category) => (
                    <option
                      key={category}
                      value={category}
                    >
                      {CATEGORY_LABELS[
                        category
                      ] || category}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          <div className="agenda-layout">

            <div className="agenda-calendar-card">
              <div className="agenda-calendar-header">
                <div>
                  <span>
                    Calendário
                  </span>

                  <h2>
                    {
                      MONTHS[
                        currentDate.getMonth()
                      ]
                    }{" "}
                    {currentDate.getFullYear()}
                  </h2>
                </div>

                <div className="agenda-calendar-controls">
                  <button
                    type="button"
                    onClick={goToday}
                    className="agenda-today"
                  >
                    Hoje
                  </button>

                  <button
                    type="button"
                    onClick={previousMonth}
                    aria-label="Mês anterior"
                  >
                    <ChevronLeft />
                  </button>

                  <button
                    type="button"
                    onClick={nextMonth}
                    aria-label="Próximo mês"
                  >
                    <ChevronRight />
                  </button>
                </div>
              </div>

              <div className="agenda-weekdays">
                {WEEKDAYS.map(
                  (day) => (
                    <div key={day}>
                      {day}
                    </div>
                  )
                )}
              </div>

              <div className="agenda-calendar-grid">
                {days.map(
                  (date, index) => {
                    if (!date) {
                      return (
                        <div
                          key={`empty-${index}`}
                          className="agenda-day agenda-day-empty"
                        />
                      );
                    }

                    const dayEvents =
                      filteredItems.filter(
                        (item) =>
                          eventOccursOn(
                            item,
                            date
                          )
                      );

                    const isToday =
                      sameDay(
                        date,
                        today
                      );

                    const isSelected =
                      selectedDay &&
                      sameDay(
                        date,
                        selectedDay
                      );

                    return (
                      <button
                        key={isoDate(date)}
                        type="button"
                        className={[
                          "agenda-day",
                          isToday
                            ? "today"
                            : "",
                          isSelected
                            ? "selected"
                            : "",
                          dayEvents.length
                            ? "has-events"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() =>
                          setSelectedDay(
                            date
                          )
                        }
                      >
                        <span className="agenda-day-number">
                          {date.getDate()}
                        </span>

                        {dayEvents.length >
                          0 && (
                          <div className="agenda-day-events">
                            {dayEvents
                              .slice(0, 2)
                              .map(
                                (item) => (
                                  <span
                                    key={
                                      item.id
                                    }
                                    className={`agenda-mini-event ${item.tipo_local || ""}`}
                                  >
                                    {
                                      item.titulo
                                    }
                                  </span>
                                )
                              )}

                            {dayEvents.length >
                              2 && (
                              <small>
                                +
                                {dayEvents.length -
                                  2}{" "}
                                atividade
                                {dayEvents.length -
                                  2 >
                                1
                                  ? "s"
                                  : ""}
                              </small>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  }
                )}
              </div>
            </div>


          </div>

          <div className="agenda-list-section">
            <div className="agenda-list-title">
              <div>
                <span className="hub-section-label">
                  PROGRAMAÇÃO
                </span>

                <h2>
                  {selectedDay
                    ? "Atividades selecionadas"
                    : "Atividades do mês"}
                </h2>
              </div>
            </div>

            {loading && (
              <div className="agenda-state">
                Carregando agenda...
              </div>
            )}

            {!loading && error && (
              <div className="agenda-state agenda-error">
                {error}
              </div>
            )}

            {!loading &&
              !error &&
              visibleItems.length === 0 && (
                <div className="agenda-state">
                  <CalendarDays />

                  <strong>
                    Nenhuma atividade encontrada
                  </strong>

                  <span>
                    Não há atividades para este
                    período com os filtros
                    selecionados.
                  </span>
                </div>
              )}

            {!loading &&
              !error &&
              visibleItems.length > 0 && (
                <div className="agenda-event-list">
                  {visibleItems.map(
                    (item) => (
                      <article
                        key={item.id}
                        className="agenda-event-card"
                      >
                        <div
                          className={`agenda-event-date ${item.tipo_local || ""}`}
                        >
                          <span>
                            {formatShortDate(
                              item.data_inicio
                            )}
                          </span>

                          {item.data_fim &&
                            item.data_fim !==
                              item.data_inicio && (
                              <small>
                                até{" "}
                                {formatShortDate(
                                  item.data_fim
                                )}
                              </small>
                            )}
                        </div>

                        <div className="agenda-event-body">
                          <div className="agenda-event-tags">
                            <span>
                              {CATEGORY_LABELS[
                                item.categoria
                              ] ||
                                item.categoria}
                            </span>

                            <span
                              className={
                                item.tipo_local ===
                                "hub"
                                  ? "hub-tag"
                                  : "external-tag"
                              }
                            >
                              {item.tipo_local ===
                              "hub"
                                ? "Interna no HUB"
                                : "Externa com apoio do HUB"}
                            </span>

                            {item.modalidade && (
                              <span>
                                {item.modalidade ===
                                "individual"
                                  ? "Individual"
                                  : "Coletiva"}
                              </span>
                            )}
                          </div>

                          <h3>
                            {item.titulo}
                          </h3>

                          {item.programa && (
                            <div className="agenda-program">
                              {item.programa}
                            </div>
                          )}

                          {item.descricao && (
                            <p>
                              {item.descricao}
                            </p>
                          )}

                          <div className="agenda-event-meta">
                            <div>
                              <CalendarDays />
                              <span>
                                {formatPeriod(
                                  item
                                )}
                              </span>
                            </div>

                            <div>
                              <Clock3 />
                              <span>
                                {formatTime(
                                  item
                                )}
                              </span>
                            </div>

                            {item.tipo_local ===
                              "hub" && (
                              <div>
                                <MapPin />
                                <span>
                                  HUB Acre
                                  {" • "}
                                  {item.espaco_hub ===
                                  "outro"
                                    ? (
                                        item.espaco_hub_outro ||
                                        "Outro espaço"
                                      )
                                    : (
                                        HUB_SPACE_LABELS[
                                          item.espaco_hub
                                        ] ||
                                        "Espaço a definir"
                                      )
                                  }
                                </span>
                              </div>
                            )}

                            {item.tipo_local ===
                              "externa" &&
                              (item.local ||
                                item.municipio) && (
                              <div>
                                <MapPin />
                                <span>
                                  {[
                                    item.local,
                                    item.municipio,
                                  ]
                                    .filter(
                                      Boolean
                                    )
                                    .join(
                                      " • "
                                    )}
                                </span>
                              </div>
                            )}

                            {item.responsavel && (
                              <div>
                                <UserRound />
                                <span>
                                  {
                                    item.responsavel
                                  }
                                </span>
                              </div>
                            )}

                            {item.modalidade && (
                              <div>
                                <Users />
                                <span>
                                  {item.modalidade ===
                                  "individual"
                                    ? "Atividade individual"
                                    : "Atividade coletiva"}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </article>
                    )
                  )}
                </div>
              )}
          </div>
        </div>
      </section>

      <footer className="hub-footer">
        <div className="hub-container">
          <a
            href="/hubacre"
            className="hub-nav-logo"
          >
            <img
              src="/hubacre/logo-horizontal.png"
              alt="HUB Acre"
            />
          </a>

          <span>
            HUB Acre — Instituto IMA
          </span>
        </div>
      </footer>
    </main>
  );
}

export default HubAgenda;
